import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import {
  Box, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Typography, TextField, Grid, Button, IconButton, Autocomplete,
  Accordion, AccordionSummary, AccordionDetails, Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import PrintIcon from "@mui/icons-material/Print";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useReactToPrint } from "react-to-print";
import api from "../services/api";
import { todayISO } from "../utils/date";

const BILL_DENOMS = [100, 50, 20, 10, 5, 1];

// LineType discriminator stored in DB. (Kept "Material" backwards-compat label
// for the Payment section so any existing records still load.)
const TYPE_RECEIVED   = "Received";
const TYPE_PAYMENT    = "Material";
const TYPE_EXPENSE    = "Expense";
const TYPE_CHECK      = "Check";
const TYPE_CREDITCARD = "CreditCard";

const emptyMaster = {
  Master_ID: null,
  Date: todayISO(),
  Cashier: "",
  CashBroughtForward: 0,
  Bills100: 0, Bills50: 0, Bills20: 0, Bills10: 0, Bills5: 0, Bills1: 0,
};

const emptyRow = (LineType) => ({
  Line_ID: null,
  LineType,
  // For an Expense row: Name = who we paid (payee), ExpenseAccount = the Chart
  // of Accounts expense category, Reference = memo. Other line types only use Name.
  Name: "",
  ExpenseAccount: "",
  Reference: "",
  Amount: 0,
});

const fmt = (n) => "$" + (Number(n) || 0).toFixed(2);

/* One payment — a $10,000 check or $10,000 cash — is applied to the account's
 * open invoices oldest-first, so it lands in Daily Cash as one line per invoice
 * (5,000 + 3,000 + 2,000). And the same party often has an extra hand-typed
 * amount on the day (a $8.64 top-up alongside a $4,571.36 invoice payment) that
 * was really handed over in the SAME payment.
 *
 * Both are rolled back up into the one payment that actually happened: same
 * section + same party + same check # = one row showing the real amount, with
 * the invoices it covers named on the chip.
 *
 * Display only — nothing is written back differently. Invoice-linked rows carry
 * SourceType and the save endpoint manages those from the invoice side, and each
 * hand-typed part is kept on the merged row (ManualParts) so expandForSave()
 * below re-emits it exactly as the database had it. Pending Invoices, the
 * General Ledger and every total are unaffected. */

const round2 = (n) => Number((Number(n) || 0).toFixed(2));

/* Section + party + check number. Deliberately excludes SourceType so a
 * hand-typed row can join the invoice-linked row for the same party. The check
 * number stays in the key — two different checks are two different payments. */
const groupKey = (r) => [
  r.LineType,
  (r.Name || "").trim().toUpperCase(),
  (r.Reference || "").trim().toUpperCase(),
].join("|");

const mergeLinked = (rows) => {
  const out = [];
  const seen = new Map();

  // Pass 1 — invoice-linked rows form the groups.
  for (const r of rows) {
    if (!r.SourceType) continue;
    const key = groupKey(r);
    const at = seen.get(key);
    const amt = round2(r.Amount);
    if (at === undefined) {
      seen.set(key, out.length);
      out.push({
        ...r,
        Amount: amt,
        LinkedAmount: amt,                                   // fixed by the invoice
        SourceIDs: r.SourceMaster_ID ? [r.SourceMaster_ID] : [],
        // Every Daily Cash line behind this row. A payment split FIFO across
        // invoices writes one line each; undoing the payment has to take all
        // of them back, not just the first.
        LineIDs: r.Line_ID ? [r.Line_ID] : [],
        ManualParts: [],                                     // hand-typed extras
      });
    } else {
      const m = out[at];
      m.Amount = round2(m.Amount + amt);
      m.LinkedAmount = round2(m.LinkedAmount + amt);
      if (r.SourceMaster_ID) m.SourceIDs = [...m.SourceIDs, r.SourceMaster_ID];
      if (r.Line_ID) m.LineIDs = [...m.LineIDs, r.Line_ID];
    }
  }

  // Pass 2 — a hand-typed row joins the matching group if there is one,
  // otherwise it stays a row of its own, untouched.
  for (const r of rows) {
    if (r.SourceType) continue;
    const at = seen.get(groupKey(r));
    if (at === undefined) { out.push(r); continue; }
    const m = out[at];
    m.Amount = round2(m.Amount + round2(r.Amount));
    m.ManualParts = [...m.ManualParts, r];
  }

  return out;
};

/* The hand-typed portion of a merged row. */
const manualTotal = (row) =>
  Array.isArray(row.ManualParts)
    ? round2(row.ManualParts.reduce((s, p) => s + (Number(p.Amount) || 0), 0))
    : 0;

/* True when the row combines an invoice payment with a hand-typed amount, so
 * the total is editable even though the row is invoice-linked. */
const hasManualPart = (row) => manualTotal(row) > 0.005;

/* Chip text for a row that came from an invoice payment. */
const linkLabel = (row) => {
  const src = row.SourceType === "SaleInvoice" ? "Sale Invoice" : "Invoice";
  const ids = row.SourceIDs || (row.SourceMaster_ID ? [row.SourceMaster_ID] : []);
  const shown = ids.slice(0, 3).join(", ");
  const base = ids.length <= 1
    ? `🔗 from ${src} #${ids[0] ?? row.SourceMaster_ID}`
    : `🔗 ${ids.length} invoices: ${shown}${ids.length > 3 ? ` +${ids.length - 3} more` : ""}`;
  const extra = manualTotal(row);
  return extra > 0.005 ? `${base} + ${extra.toFixed(2)} extra` : base;
};

/* Merged display rows -> the rows the database actually stores. The
 * invoice-linked row goes back at its own amount (the endpoint ignores it, as
 * before), and every hand-typed part is emitted as its own manual line. Without
 * this, a merged row would be saved as one invoice-linked line and the
 * hand-typed amount would be dropped on the next save. */
const expandForSave = (rows) =>
  rows.flatMap((r) => {
    if (!Array.isArray(r.ManualParts)) return [r];
    const linked = { ...r, Amount: r.LinkedAmount ?? r.Amount };
    delete linked.ManualParts;
    delete linked.LinkedAmount;
    delete linked.SourceIDs;
    const parts = r.ManualParts.map((p) => ({
      ...p,
      LineType: r.LineType,
      Name: p.Name || r.Name || "",
      Reference: p.Reference ?? r.Reference ?? "",
      SourceType: null,
      SourceMaster_ID: null,
    }));
    return [linked, ...parts];
  });

/* ================= MAIN ================= */
export default function DailyCash() {
  const [editId, setEditId] = useState(null);
  return (
    <Box sx={{ width: "100%" }}>
      <DailyCashEditor editId={editId} setEditId={setEditId} />
      <DailyCashList onEdit={setEditId} />
    </Box>
  );
}

/* ================= EDITOR ================= */
function DailyCashEditor({ editId, setEditId }) {
  const [accounts, setAccounts] = useState([]);
  // Operating-expense accounts from the Chart of Accounts — these populate the
  // Expenses dropdown so each expense lands on a consistent account name that
  // the Profit & Loss groups by. Stored as plain name strings.
  const [expenseAccounts, setExpenseAccounts] = useState([]);
  const [master, setMaster] = useState(emptyMaster);
  const [received, setReceived] = useState([emptyRow(TYPE_RECEIVED)]);
  const [payments, setPayments] = useState([emptyRow(TYPE_PAYMENT)]);
  const [expenses, setExpenses] = useState([emptyRow(TYPE_EXPENSE)]);
  const [checks,   setChecks]   = useState([emptyRow(TYPE_CHECK)]);
  const [credits,  setCredits]  = useState([emptyRow(TYPE_CREDITCARD)]);

  // Auto-save plumbing. `hydrated` flips true once we've loaded today's
  // record from the server — we don't want the very first state mount to
  // trigger a redundant POST.
  const hydratedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const [autoStatus, setAutoStatus] = useState("idle"); // idle | saving | saved | error
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `Daily-Cash-${master.Date || ""}`,
    pageStyle: "@page { size: Letter; margin: 0.4in; }",
  });

  const resetForm = useCallback(() => {
    setMaster(emptyMaster);
    setReceived([emptyRow(TYPE_RECEIVED)]);
    setPayments([emptyRow(TYPE_PAYMENT)]);
    setExpenses([emptyRow(TYPE_EXPENSE)]);
    setChecks([emptyRow(TYPE_CHECK)]);
    setCredits([emptyRow(TYPE_CREDITCARD)]);
  }, []);

  /* === Load accounts === */
  useEffect(() => {
    api.get("/accounts")
      .then((r) => setAccounts(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load accounts:", e));
  }, []);

  /* === Load expense accounts from the Chart of Accounts (for the Expenses
   * dropdown). Operating/other expenses only — Cost of Goods Sold comes from
   * purchase invoices, not cash expenses. Fails silently for users without the
   * accounting module (they can still type an expense name free-hand). === */
  useEffect(() => {
    api.get("/accounting/chart")
      .then((r) => {
        const names = (Array.isArray(r.data) ? r.data : [])
          .filter((a) => a.AccountType === "Expense" && a.SubType !== "Cost of Goods Sold" && a.IsActive)
          .map((a) => a.AccountName)
          .filter(Boolean);
        setExpenseAccounts(names);
      })
      .catch(() => setExpenseAccounts([]));
  }, []);

  /* === Auto-load today's record on mount (unless editing a past one) === */
  useEffect(() => {
    if (editId) return; // edit-old flow handles its own load below
    hydratedRef.current = false;
    api.get("/dailyCash/today")
      .then((res) => {
        const m = res.data?.master || {};
        const lines = res.data?.lines || [];
        setMaster({
          Master_ID: m.Master_ID || null,
          Date: m.Date ? String(m.Date).split("T")[0] : emptyMaster.Date,
          Cashier: m.Cashier || "",
          CashBroughtForward: Number(m.CashBroughtForward) || 0,
          Bills100: Number(m.Bills100) || 0,
          Bills50:  Number(m.Bills50)  || 0,
          Bills20:  Number(m.Bills20)  || 0,
          Bills10:  Number(m.Bills10)  || 0,
          Bills5:   Number(m.Bills5)   || 0,
          Bills1:   Number(m.Bills1)   || 0,
        });
        const byType = (t) => mergeLinked(lines.filter((l) => l.LineType === t).map((l) => ({
          Line_ID: l.Line_ID, LineType: t,
          Name: l.Name || "", Reference: l.Reference || "",
          ExpenseAccount: l.ExpenseAccount || "",
          Amount: Number(l.Amount) || 0,
          SourceType: l.SourceType || null,
          SourceMaster_ID: l.SourceMaster_ID || null,
        })));
        const ensureOne = (arr, t) => (arr.length ? arr : [emptyRow(t)]);
        setReceived(ensureOne(byType(TYPE_RECEIVED),  TYPE_RECEIVED));
        setPayments(ensureOne(byType(TYPE_PAYMENT),   TYPE_PAYMENT));
        setExpenses(ensureOne(byType(TYPE_EXPENSE),   TYPE_EXPENSE));
        setChecks(ensureOne(byType(TYPE_CHECK),       TYPE_CHECK));
        setCredits(ensureOne(byType(TYPE_CREDITCARD), TYPE_CREDITCARD));
        setLastSavedAt(new Date());
      })
      .catch((err) => console.error("Failed to load today's daily cash:", err))
      .finally(() => { hydratedRef.current = true; });
  }, [editId]);

  /* === Load record for edit === */
  useEffect(() => {
    if (!editId) return;
    hydratedRef.current = false;
    api.get(`/dailyCash/${editId}`)
      .then((res) => {
        const m = res.data?.master || {};
        const lines = res.data?.lines || [];
        setMaster({
          Master_ID: m.Master_ID || null,
          Date: m.Date ? String(m.Date).split("T")[0] : emptyMaster.Date,
          Cashier: m.Cashier || "",
          CashBroughtForward: Number(m.CashBroughtForward) || 0,
          Bills100: Number(m.Bills100) || 0,
          Bills50:  Number(m.Bills50)  || 0,
          Bills20:  Number(m.Bills20)  || 0,
          Bills10:  Number(m.Bills10)  || 0,
          Bills5:   Number(m.Bills5)   || 0,
          Bills1:   Number(m.Bills1)   || 0,
        });
        const byType = (t) => mergeLinked(lines.filter((l) => l.LineType === t).map((l) => ({
          Line_ID: l.Line_ID, LineType: t,
          Name: l.Name || "", Reference: l.Reference || "",
          ExpenseAccount: l.ExpenseAccount || "",
          Amount: Number(l.Amount) || 0,
          SourceType: l.SourceType || null,
          SourceMaster_ID: l.SourceMaster_ID || null,
        })));
        const ensureOne = (arr, t) => (arr.length ? arr : [emptyRow(t)]);
        setReceived(ensureOne(byType(TYPE_RECEIVED),   TYPE_RECEIVED));
        setPayments(ensureOne(byType(TYPE_PAYMENT),    TYPE_PAYMENT));
        setExpenses(ensureOne(byType(TYPE_EXPENSE),    TYPE_EXPENSE));
        setChecks(ensureOne(byType(TYPE_CHECK),        TYPE_CHECK));
        setCredits(ensureOne(byType(TYPE_CREDITCARD),  TYPE_CREDITCARD));
        setLastSavedAt(new Date());
      })
      .catch((err) => {
        console.error("Failed to load daily cash:", err);
        resetForm();
      })
      .finally(() => { hydratedRef.current = true; });
  }, [editId, resetForm]);

  /* === Row helpers === */
  const updateRow = (setter) => (idx, field, value) => {
    setter((prev) => {
      const rows = [...prev];
      const next = { ...rows[idx] };
      const isMerged = !!next.SourceType && Array.isArray(next.ManualParts);
      const linked = round2(next.LinkedAmount);

      if (field === "ManualParts") {
        // Removing the hand-typed part of a merged row leaves just the amount
        // the invoice itself recorded.
        next.ManualParts = [];
        next.Amount = linked;
      } else if (field === "Amount" && isMerged) {
        // The figure typed here is the TOTAL handed over. The invoice-linked
        // portion is fixed by the invoice, so the difference is the hand-typed
        // part. It can't go negative — a total below what the invoice recorded
        // has to be changed on the invoice itself.
        const typed = value === "" ? 0 : Number(value);
        const manual = round2(Math.max(0, typed - linked));
        next.Amount = round2(linked + manual);
        next.ManualParts = manual > 0.005
          ? [{
              ...(next.ManualParts[0] || {}),
              Line_ID:  next.ManualParts[0]?.Line_ID ?? null,
              LineType: next.LineType,
              Name:     next.Name || "",
              Reference: next.Reference || "",
              Amount:   manual,
              SourceType: null,
              SourceMaster_ID: null,
            }]
          : [];
      } else if (field === "Amount") {
        next.Amount = value === "" ? 0 : Number(value);
      } else {
        next[field] = value;
      }
      rows[idx] = next;
      return rows;
    });
  };
  const addRow = (setter, type) => () => setter((p) => [...p, emptyRow(type)]);
  const removeRow = (setter) => (idx) => setter((p) => {
    const next = p.filter((_, i) => i !== idx);
    return next.length ? next : [emptyRow(p[0]?.LineType || TYPE_RECEIVED)];
  });

  /* Take back a payment that was recorded against an invoice.
   *
   * These rows are locked here because the invoice owns them, and until now
   * nothing anywhere removed an invoice payment — so a check written to the
   * wrong supplier, or for the wrong amount, stayed on the books for good.
   *
   * The server deletes the invoice payment and the Daily Cash line together,
   * gives back any advance credit it had used, and refuses outright if the
   * invoice has since been assigned to a broker. A payment split across
   * several invoices wrote one Daily Cash line each, so every line behind
   * this row goes back, not just the first. */
  const undoLinkedPayment = async (row) => {
    const lineIds = row.LineIDs && row.LineIDs.length
      ? row.LineIDs
      : (row.Line_ID ? [row.Line_ID] : []);
    if (!lineIds.length) return;

    const what = row.LineType === TYPE_CHECK
      ? `check${row.Reference ? ` #${row.Reference}` : ""}`
      : "payment";
    const ok = window.confirm(
      `Undo this ${what} of $${Number(row.Amount || 0).toFixed(2)} to ` +
      `${(row.Name || "").trim()}?\n\n` +
      `The invoice${(row.SourceIDs || []).length > 1 ? "s" : ""} ` +
      `${(row.SourceIDs || []).join(", ")} will go back to unpaid and the money ` +
      `returns to the drawer. This cannot be undone from here.`);
    if (!ok) return;

    try {
      const messages = [];
      for (const id of lineIds) {
        const r = await api.delete(`/invoicePayments/by-cash-line/${id}`);
        if (r.data?.message) messages.push(r.data.message);
      }
      alert(messages.join("\n") || "Payment removed.");
      window.location.reload();
    } catch (err) {
      alert(err.response?.data?.error || err.message || "Could not remove the payment.");
    }
  };

  /* === Debounced auto-save: any state change → PUT after 2 sec of idle === */
  useEffect(() => {
    if (!hydratedRef.current) return;       // skip the initial mount
    if (!master.Master_ID) return;          // need a master id to PUT
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        setAutoStatus("saving");
        const billsTotalLocal =
          100 * (Number(master.Bills100) || 0) +
           50 * (Number(master.Bills50)  || 0) +
           20 * (Number(master.Bills20)  || 0) +
           10 * (Number(master.Bills10)  || 0) +
            5 * (Number(master.Bills5)   || 0) +
            1 * (Number(master.Bills1)   || 0);
        const lines = expandForSave([...received, ...payments, ...expenses, ...checks, ...credits])
          .filter((r) =>
            (r.Name && String(r.Name).trim() !== "") ||
            Number(r.Amount) > 0 ||
            (r.Reference && String(r.Reference).trim() !== "") ||
            (r.ExpenseAccount && String(r.ExpenseAccount).trim() !== "")
          );
        await api.put(`/dailyCash/${master.Master_ID}`, {
          master: { ...master, RegisterBalance: billsTotalLocal },
          lines,
        });
        setAutoStatus("saved");
        setLastSavedAt(new Date());
      } catch (err) {
        console.error("Auto-save failed:", err);
        setAutoStatus("error");
      }
    }, 2000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [master, received, payments, expenses, checks, credits]);

  /* === Totals + live running balance === */
  const sumAmount = (rows) => rows.reduce((s, r) => s + (Number(r.Amount) || 0), 0);

  const receivedTotal = sumAmount(received);
  const paymentTotal  = sumAmount(payments);
  const expenseTotal  = sumAmount(expenses);
  const checkTotal    = sumAmount(checks);
  const creditTotal   = sumAmount(credits);

  const broughtForward = Number(master.CashBroughtForward) || 0;
  const cashAvailable  = broughtForward + receivedTotal;
  const cashSpent      = paymentTotal + expenseTotal;
  const currentBalance = cashAvailable - cashSpent;          // Balance as should be

  const billsBreakdown = useMemo(() => BILL_DENOMS.map((d) => ({
    denom: d, count: Number(master[`Bills${d}`]) || 0,
    amount: d * (Number(master[`Bills${d}`]) || 0),
  })), [master]);
  const billsTotal = billsBreakdown.reduce((s, b) => s + b.amount, 0);
  // Cash Balance as per Register = total of the bill count.
  const registerBalance = billsTotal;
  const overShort = registerBalance - currentBalance;

  /* === Save === */
  const save = async () => {
    if (!master.Date) { alert("Pick a date"); return; }
    const lines = expandForSave([...received, ...payments, ...expenses, ...checks, ...credits])
      .filter((r) =>
        (r.Name && String(r.Name).trim() !== "") ||
        Number(r.Amount) > 0 ||
        (r.Reference && String(r.Reference).trim() !== "")
      );
    try {
      // RegisterBalance is derived from bills count — persist that to the DB.
      const payload = {
        master: { ...master, RegisterBalance: billsTotal },
        lines,
      };
      const res = editId
        ? await api.put(`/dailyCash/${editId}`, payload)
        : await api.post("/dailyCash", payload);
      alert(res.data?.message || "Saved");
      resetForm();
      setEditId(null);
    } catch (err) {
      console.error("Save daily cash failed:", err);
      alert("Failed to save: " + (err.response?.data?.error || err.message));
    }
  };

  const cancelEdit = () => { resetForm(); setEditId(null); };

  return (
    <Box p={1}>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {editId ? "Edit Daily Cash Report" : "Daily Cash Report"}
            </Typography>
            <AutoSaveBadge status={autoStatus} lastSavedAt={lastSavedAt} />
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="outlined" startIcon={<PrintIcon />} onClick={handlePrint}>Print</Button>
            <Button variant="contained" onClick={save}>{editId ? "Update" : "Save now"}</Button>
            {editId && <Button onClick={cancelEdit}>Cancel</Button>}
          </Box>
        </Box>

        {/* Header info */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField label="Cashier" fullWidth value={master.Cashier}
              onChange={(e) => setMaster({ ...master, Cashier: e.target.value })} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField type="date" label="Date" fullWidth InputLabelProps={{ shrink: true }}
              value={master.Date}
              onChange={(e) => setMaster({ ...master, Date: e.target.value })} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 4 }}>
            <TextField type="number" label="Cash Brought Forward" fullWidth
              value={master.CashBroughtForward}
              InputProps={{ readOnly: true }}
              helperText="Auto: previous day's closing cash"
              inputProps={{ step: "0.01", min: "0" }}
            />
          </Grid>
        </Grid>

        {/* Live running balance strip */}
        <Paper elevation={3} sx={{ p: 2, mb: 3, bgcolor: "primary.dark", color: "primary.contrastText" }}>
          <Grid container spacing={2} alignItems="center">
            <Stat label="Brought Forward"           value={broughtForward}        />
            <Stat label="+ Received"                value={receivedTotal}    plus />
            <Stat label="= Cash Available"          value={cashAvailable}    bold />
            <Stat label="− Payments"                value={paymentTotal}    minus />
            <Stat label="− Expenses"                value={expenseTotal}    minus />
            <Stat label="= Balance Should Be"       value={currentBalance}   bold highlight />
          </Grid>
        </Paper>

        {/* === Section 1: Cash Received === */}
        <Section
          title="1. Cash Received"
          totalLabel="Total Received"
          total={receivedTotal}
          color="success.main"
        >
          {received.map((r, i) => (
            <AccountAmountRow
              key={i}
              row={r}
              idx={i}
              accounts={accounts}
              onUpdate={updateRow(setReceived)}
              onRemove={() => removeRow(setReceived)(i)}
              onUndoLinked={undoLinkedPayment}
              placeholder="Who paid us"
            />
          ))}
          <Button size="small" onClick={addRow(setReceived, TYPE_RECEIVED)}>+ Add Received</Button>
        </Section>

        {/* === Section 2: Cash Payment === */}
        <Section
          title="2. Cash Payment"
          totalLabel="Total Paid"
          total={paymentTotal}
          color="error.main"
        >
          {payments.map((r, i) => (
            <AccountAmountRow
              key={i}
              row={r}
              idx={i}
              accounts={accounts}
              onUpdate={updateRow(setPayments)}
              onRemove={() => removeRow(setPayments)(i)}
              onUndoLinked={undoLinkedPayment}
              placeholder="Who we paid"
            />
          ))}
          <Button size="small" onClick={addRow(setPayments, TYPE_PAYMENT)}>+ Add Payment</Button>
        </Section>

        {/* === Section 3: Expenses === */}
        <Section
          title="3. Expenses"
          totalLabel="Total Expenses"
          total={expenseTotal}
          color="error.main"
        >
          <Typography variant="caption" sx={{ display: "block", mb: 1, color: "text.secondary" }}>
            <b>Paid to</b> = who got the money (any name). <b>Expense account</b> = the
            category it belongs to (Office Expense, Repair &amp; Maintenance, …) — this is
            what groups it on the Profit &amp; Loss. Add expense accounts under
            Accounting → Chart of Accounts, or just type one here.
          </Typography>
          {expenses.map((r, i) => (
            <ExpenseRow
              key={i}
              row={r}
              idx={i}
              accounts={accounts}
              expenseAccounts={expenseAccounts}
              onUpdate={updateRow(setExpenses)}
              onRemove={() => removeRow(setExpenses)(i)}
            />
          ))}
          <Button size="small" onClick={addRow(setExpenses, TYPE_EXPENSE)}>+ Add Expense</Button>
        </Section>

        {/* === Section 4: Checks === */}
        <Section
          title="4. Checks"
          totalLabel="Total Checks"
          total={checkTotal}
        >
          {checks.map((r, i) => (
            <CheckRow
              key={i}
              row={r}
              idx={i}
              accounts={accounts}
              onUpdate={updateRow(setChecks)}
              onRemove={() => removeRow(setChecks)(i)}
              onUndoLinked={undoLinkedPayment}
            />
          ))}
          <Button size="small" onClick={addRow(setChecks, TYPE_CHECK)}>+ Add Check</Button>
        </Section>

        {/* === Section 5: Bills (cash count) === */}
        <Section
          title="5. Bills (cash count)"
          totalLabel="Total Bills"
          total={billsTotal}
        >
          <Table size="small" sx={{ maxWidth: 600 }}>
            <TableHead>
              <TableRow>
                <TableCell>Denomination</TableCell>
                <TableCell align="right">Count</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {BILL_DENOMS.map((d) => (
                <TableRow key={d}>
                  <TableCell>${d}</TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small" type="number"
                      value={master[`Bills${d}`] ?? 0}
                      onChange={(e) => setMaster({ ...master, [`Bills${d}`]: Number(e.target.value) || 0 })}
                      inputProps={{ step: "1", min: "0" }}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell align="right">{fmt(d * (Number(master[`Bills${d}`]) || 0))}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Section>

        {/* === Section 6: Credit Cards (optional) === */}
        <Section
          title="6. Credit Cards"
          totalLabel="Total Credit Cards"
          total={creditTotal}
          defaultExpanded={false}
        >
          {credits.map((r, i) => (
            <AccountAmountRow
              key={i}
              row={r}
              idx={i}
              accounts={accounts}
              allowFreeText
              onUpdate={updateRow(setCredits)}
              onRemove={() => removeRow(setCredits)(i)}
              placeholder="Card / source"
            />
          ))}
          <Button size="small" onClick={addRow(setCredits, TYPE_CREDITCARD)}>+ Add Credit Card</Button>
        </Section>

        {/* Reconciliation strip */}
        <Paper sx={{ p: 2, mt: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  Cash Balance as per Register (from bills count)
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmt(registerBalance)}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: "action.hover" }}>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>Balance Should Be</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmt(currentBalance)}</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <Box sx={{
                p: 1.5, borderRadius: 1,
                bgcolor: Math.abs(overShort) < 0.01 ? "success.light" : "error.light",
                color: Math.abs(overShort) < 0.01 ? "success.dark"  : "error.dark",
              }}>
                <Typography variant="caption">Over / Short</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{fmt(overShort)}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Box mt={2} sx={{ display: "flex", gap: 1 }}>
          <Button variant="contained" onClick={save}>{editId ? "Update" : "Save"}</Button>
          {editId && <Button onClick={cancelEdit}>Cancel</Button>}
        </Box>
      </Paper>

      {/* Hidden printable view */}
      <Box sx={{ display: "none", "@media print": { display: "block" } }}>
        <DailyCashPrintView
          ref={printRef}
          master={master}
          received={received} payments={payments} expenses={expenses}
          checks={checks} credits={credits}
          totals={{
            receivedTotal, paymentTotal, expenseTotal, checkTotal, creditTotal,
            cashAvailable, cashSpent, currentBalance, overShort,
            billsTotal, billsBreakdown,
          }}
        />
      </Box>
    </Box>
  );
}

/* ----- Auto-save status badge ----- */
function AutoSaveBadge({ status, lastSavedAt }) {
  if (status === "saving") {
    return <Chip size="small" label="Saving…" color="warning" variant="outlined" />;
  }
  if (status === "error") {
    return <Chip size="small" label="⚠ Couldn't save — will retry" color="error" />;
  }
  if (lastSavedAt) {
    const t = lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    return <Chip size="small" label={`✓ Saved ${t}`} color="success" variant="outlined" />;
  }
  return <Chip size="small" label="Auto-saves as you type" variant="outlined" />;
}

/* ----- Collapsible section wrapper ----- */
function Section({ title, total, totalLabel, color, defaultExpanded = false, children }) {
  return (
    <Accordion defaultExpanded={defaultExpanded} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", pr: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
          <Chip
            label={`${totalLabel}: ${fmt(total)}`}
            sx={{
              fontWeight: 700,
              bgcolor: color || "primary.light",
              color: color ? "#fff" : "primary.dark",
            }}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

/* ----- Single row: account picker + amount + remove ----- */
function AccountAmountRow({ row, idx, accounts, onUpdate, onRemove, onUndoLinked, placeholder, allowFreeText }) {
  const isLinked = !!row.SourceType;
  // A merged row (invoice payment + hand-typed extra) keeps its amount editable:
  // what's typed is the total handed over, and the difference lands on the
  // hand-typed part. The party stays locked to the invoice either way.
  const hasManual = hasManualPart(row);
  const amountLocked = isLinked && !hasManual;
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Autocomplete
          freeSolo={!!allowFreeText}
          disabled={isLinked}
          options={accounts}
          getOptionLabel={(o) => (typeof o === "string" ? o : (o?.SubAccount_Name || ""))}
          value={row.Name || ""}
          onChange={(e, v) => {
            const name = typeof v === "string" ? v : (v?.SubAccount_Name || "");
            onUpdate(idx, "Name", name);
          }}
          onInputChange={(e, v) => {
            if (allowFreeText) onUpdate(idx, "Name", v || "");
          }}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder={placeholder || "Select account"} fullWidth />
          )}
        />
      </Box>
      <TextField
        size="small" type="number"
        disabled={amountLocked}
        value={row.Amount}
        onChange={(e) => onUpdate(idx, "Amount", e.target.value)}
        inputProps={{ step: "0.01", min: "0" }}
        sx={{ width: 140 }}
        placeholder="Amount"
      />
      {isLinked ? (
        <Chip size="small" variant="outlined" color="primary"
          label={hasManual ? linkLabel(row) : `${linkLabel(row)} — edit there`}
          sx={{ flex: "1 0 auto" }}
        />
      ) : null}
      {!isLinked ? (
        <IconButton color="error" size="small" onClick={onRemove}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) : hasManual ? (
        // Clears only the hand-typed extra; the invoice payment stays.
        <IconButton color="error" size="small" title="Remove the extra amount"
          onClick={() => onUpdate(idx, "ManualParts", [])}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) : onUndoLinked ? (
        /* A payment recorded against an invoice. Until now this row had no
         * way out at all: it is locked because the invoice owns it, and
         * nothing removed an invoice payment. So a check written to the wrong
         * supplier stayed on the books for good. This takes it back — the
         * invoice re-opens and the cash returns to the drawer. */
        <IconButton color="error" size="small"
          title="Undo this payment — the invoice re-opens"
          onClick={() => onUndoLinked(row)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
}

/* ----- Expense row: payee (Name) + expense account (ExpenseAccount) + memo
 * (Reference) + amount. Both pickers are freeSolo so a user can type a value
 * that isn't in the list (and users without the accounting module, who get an
 * empty expenseAccounts list, can still type the category). ----- */
function ExpenseRow({ row, idx, accounts = [], expenseAccounts = [], onUpdate, onRemove }) {
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
      <Box sx={{ flex: 1, minWidth: 170 }}>
        <Autocomplete
          freeSolo
          options={accounts}
          getOptionLabel={(o) => (typeof o === "string" ? o : (o?.SubAccount_Name || ""))}
          value={row.Name || ""}
          onChange={(e, v) => {
            const name = typeof v === "string" ? v : (v?.SubAccount_Name || "");
            onUpdate(idx, "Name", name);
          }}
          onInputChange={(e, v) => onUpdate(idx, "Name", v || "")}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="Paid to" fullWidth />
          )}
        />
      </Box>
      <Box sx={{ flex: 1, minWidth: 170 }}>
        <Autocomplete
          freeSolo
          options={expenseAccounts}
          getOptionLabel={(o) => (typeof o === "string" ? o : (o?.AccountName || ""))}
          value={row.ExpenseAccount || ""}
          onChange={(e, v) => {
            const name = typeof v === "string" ? v : (v?.AccountName || "");
            onUpdate(idx, "ExpenseAccount", name);
          }}
          onInputChange={(e, v) => onUpdate(idx, "ExpenseAccount", v || "")}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="Expense account" fullWidth />
          )}
        />
      </Box>
      <TextField
        size="small"
        value={row.Reference || ""}
        onChange={(e) => onUpdate(idx, "Reference", e.target.value)}
        placeholder="Memo"
        sx={{ flex: 1, minWidth: 140 }}
      />
      <TextField
        size="small" type="number"
        value={row.Amount}
        onChange={(e) => onUpdate(idx, "Amount", e.target.value)}
        inputProps={{ step: "0.01", min: "0" }}
        sx={{ width: 130 }}
        placeholder="Amount"
      />
      <IconButton color="error" size="small" onClick={onRemove}>
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
}

/* ----- Check row: company + check# + amount ----- */
function CheckRow({ row, idx, accounts, onUpdate, onRemove, onUndoLinked }) {
  const isLinked = !!row.SourceType;
  const hasManual = hasManualPart(row);
  const amountLocked = isLinked && !hasManual;
  return (
    <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center", flexWrap: "wrap" }}>
      <Box sx={{ flex: 1, minWidth: 180 }}>
        <Autocomplete
          freeSolo
          disabled={isLinked}
          options={accounts}
          getOptionLabel={(o) => (typeof o === "string" ? o : (o?.SubAccount_Name || ""))}
          value={row.Name || ""}
          onChange={(e, v) => {
            const name = typeof v === "string" ? v : (v?.SubAccount_Name || "");
            onUpdate(idx, "Name", name);
          }}
          onInputChange={(e, v) => onUpdate(idx, "Name", v || "")}
          renderInput={(params) => (
            <TextField {...params} size="small" placeholder="Company" fullWidth />
          )}
        />
      </Box>
      <TextField
        size="small"
        disabled={isLinked}
        value={row.Reference || ""}
        onChange={(e) => onUpdate(idx, "Reference", e.target.value)}
        placeholder="Check #"
        sx={{ width: 130 }}
      />
      <TextField
        size="small" type="number"
        disabled={amountLocked}
        value={row.Amount}
        onChange={(e) => onUpdate(idx, "Amount", e.target.value)}
        inputProps={{ step: "0.01", min: "0" }}
        sx={{ width: 140 }}
        placeholder="Amount"
      />
      {isLinked ? (
        <Chip size="small" variant="outlined" color="primary" label={linkLabel(row)} />
      ) : null}
      {!isLinked ? (
        <IconButton color="error" size="small" onClick={onRemove}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) : hasManual ? (
        <IconButton color="error" size="small" title="Remove the extra amount"
          onClick={() => onUpdate(idx, "ManualParts", [])}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) : onUndoLinked ? (
        <IconButton color="error" size="small"
          title="Undo this check — the invoice re-opens"
          onClick={() => onUndoLinked(row)}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
}

/* ----- Stat tile inside the running-balance strip ----- */
function Stat({ label, value, plus, minus, bold, highlight }) {
  const sign = plus ? "+ " : minus ? "− " : "";
  return (
    <Grid size={{ xs: 6, sm: 4, md: 2 }}>
      <Box sx={{ p: 1, borderRadius: 1, bgcolor: highlight ? "warning.main" : "transparent", color: highlight ? "warning.contrastText" : "inherit" }}>
        <Typography variant="caption" sx={{ opacity: 0.85 }}>{label}</Typography>
        <Typography sx={{ fontWeight: bold ? 700 : 500, fontSize: bold ? 20 : 16 }}>
          {sign}{fmt(value).replace("$", "$")}
        </Typography>
      </Box>
    </Grid>
  );
}

/* ================= LIST ================= */
function DailyCashList({ onEdit }) {
  const today = todayISO();
  // Default both ends of the range to today; the user can widen it whenever
  // they need to see older reports.
  const [rows, setRows] = useState([]);
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const [cashier, setCashier] = useState("");

  const fetchRows = useCallback(() => {
    api.get("/dailyCash", { params: { fromDate, toDate, cashier } })
      .then((r) => setRows(Array.isArray(r.data) ? r.data : []))
      .catch((e) => console.error("Failed to load daily cash:", e));
  }, [fromDate, toDate, cashier]);

  useEffect(() => fetchRows(), [fetchRows]);

  const del = async (id) => {
    if (!window.confirm("Delete this daily cash entry?")) return;
    try {
      await api.delete(`/dailyCash/${id}`);
      setRows((p) => p.filter((x) => x.Master_ID !== id));
    } catch (err) {
      alert("Delete failed: " + (err.response?.data?.error || err.message));
    }
  };

  return (
    <Box p={1}>
      <Typography variant="h6" sx={{ mb: 1 }}>Past Daily Cash Reports</Typography>
      <Paper sx={{ p: 2, mb: 1 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4, md: 3 }}>
            <TextField label="Cashier" fullWidth value={cashier} onChange={(e) => setCashier(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField type="date" label="From" InputLabelProps={{ shrink: true }} fullWidth
              value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 6, sm: 3, md: 2 }}>
            <TextField type="date" label="To" InputLabelProps={{ shrink: true }} fullWidth
              value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button variant="contained" fullWidth onClick={fetchRows}>Search</Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Cashier</TableCell>
              <TableCell align="right">Received</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Expenses</TableCell>
              <TableCell align="right">Register</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ color: "text.secondary" }}>
                  No daily cash entries
                </TableCell>
              </TableRow>
            ) : rows.map((r) => (
              <TableRow key={r.Master_ID}>
                <TableCell>{r.Master_ID}</TableCell>
                <TableCell>{r.Date ? String(r.Date).split("T")[0] : "-"}</TableCell>
                <TableCell>{r.Cashier || "-"}</TableCell>
                <TableCell align="right">{Number(r.ReceivedTotal || 0).toFixed(2)}</TableCell>
                <TableCell align="right">{Number(r.MaterialTotal || 0).toFixed(2)}</TableCell>
                <TableCell align="right">{Number(r.ExpenseTotal  || 0).toFixed(2)}</TableCell>
                <TableCell align="right">{Number(r.RegisterBalance || 0).toFixed(2)}</TableCell>
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  <Button size="small" variant="outlined" onClick={() => onEdit(r.Master_ID)} sx={{ mr: 1 }}>
                    Edit
                  </Button>
                  <Button size="small" variant="outlined" color="error" onClick={() => del(r.Master_ID)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
}

/* ================= PRINTABLE VIEW ================= */
const DailyCashPrintView = React.forwardRef(function DailyCashPrintView(
  { master, received, payments, expenses, checks, credits, totals }, ref,
) {
  const td = { border: "1px solid #888", padding: "3px 6px", fontSize: 10 };
  const th = { ...td, background: "#eee", fontWeight: 700 };

  // withRef adds a second text column (Check # for checks, Memo for expenses).
  const Section = ({ title, rows, withRef, refLabel = "Check #" }) => (
    <>
      <div style={{ fontWeight: 700, fontSize: 11, margin: "10px 0 4px" }}>{title}</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 30 }}>#</th>
            <th style={{ ...th, textAlign: "left" }}>Account / Name</th>
            {withRef && <th style={{ ...th, textAlign: "left", width: 140 }}>{refLabel}</th>}
            <th style={{ ...th, textAlign: "right", width: 100 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.filter((r) =>
            (r.Name && r.Name.trim()) ||
            (r.Reference && r.Reference.trim()) ||
            Number(r.Amount) > 0
          ).map((r, i) => (
            <tr key={i}>
              <td style={td}>{i + 1}</td>
              <td style={td}>{r.Name || ""}</td>
              {withRef && <td style={td}>{r.Reference || ""}</td>}
              <td style={{ ...td, textAlign: "right" }}>{fmt(r.Amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );

  return (
    <div ref={ref} style={{ fontFamily: "Arial, sans-serif", color: "#000", padding: 12, fontSize: 11 }}>
      <div style={{ textAlign: "center", marginBottom: 6 }}>
        <div style={{ fontWeight: 700, fontSize: 18 }}>GLOMETS INC</div>
        <div style={{ fontSize: 10 }}>2150 GAULT RD &nbsp;·&nbsp; HOUSTON, TX 77039-1705</div>
        <div style={{ fontWeight: 700, fontSize: 13, marginTop: 4 }}>DAILY CASH REPORT</div>
      </div>

      <table style={{ width: "100%", marginBottom: 8 }}>
        <tbody>
          <tr>
            <td style={{ padding: 2, width: "20%" }}><b>Cashier:</b></td>
            <td style={{ padding: 2, width: "30%" }}>{master.Cashier || "-"}</td>
            <td style={{ padding: 2, width: "20%" }}><b>Date:</b></td>
            <td style={{ padding: 2, width: "30%" }}>{master.Date || "-"}</td>
          </tr>
        </tbody>
      </table>

      <Section title="1. Cash Received"  rows={received} />
      <div style={{ textAlign: "right", fontWeight: 700, marginTop: 2 }}>Total Received: {fmt(totals.receivedTotal)}</div>

      <Section title="2. Cash Payment"   rows={payments} />
      <div style={{ textAlign: "right", fontWeight: 700, marginTop: 2 }}>Total Paid: {fmt(totals.paymentTotal)}</div>

      {/* Expenses print: Account + Paid To + Memo + Amount */}
      <div style={{ fontWeight: 700, fontSize: 11, margin: "10px 0 4px" }}>3. Expenses</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...th, width: 30 }}>#</th>
            <th style={{ ...th, textAlign: "left" }}>Expense Account</th>
            <th style={{ ...th, textAlign: "left", width: 150 }}>Paid To</th>
            <th style={{ ...th, textAlign: "left", width: 140 }}>Memo</th>
            <th style={{ ...th, textAlign: "right", width: 100 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {expenses.filter((r) =>
            (r.Name && r.Name.trim()) || (r.ExpenseAccount && r.ExpenseAccount.trim()) ||
            (r.Reference && r.Reference.trim()) || Number(r.Amount) > 0
          ).map((r, i) => (
            <tr key={i}>
              <td style={td}>{i + 1}</td>
              <td style={td}>{r.ExpenseAccount || ""}</td>
              <td style={td}>{r.Name || ""}</td>
              <td style={td}>{r.Reference || ""}</td>
              <td style={{ ...td, textAlign: "right" }}>{fmt(r.Amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div style={{ textAlign: "right", fontWeight: 700, marginTop: 2 }}>Total Expenses: {fmt(totals.expenseTotal)}</div>

      <Section title="4. Checks"         rows={checks} withRef />
      <div style={{ textAlign: "right", fontWeight: 700, marginTop: 2 }}>Total Checks: {fmt(totals.checkTotal)}</div>

      {credits.some((r) => (r.Name && r.Name.trim()) || Number(r.Amount) > 0) && (
        <>
          <Section title="5. Credit Cards" rows={credits} />
          <div style={{ textAlign: "right", fontWeight: 700, marginTop: 2 }}>Total Credit Cards: {fmt(totals.creditTotal)}</div>
        </>
      )}

      {/* Summary box */}
      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12, fontSize: 11 }}>
        <tbody>
          <tr><td style={td}>Cash Brought Forward</td><td style={{ ...td, textAlign: "right" }}>{fmt(master.CashBroughtForward)}</td></tr>
          <tr><td style={{ ...td, fontWeight: 700 }}>Cash Available for the Day</td><td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmt(totals.cashAvailable)}</td></tr>
          <tr><td style={td}>Cash Spend for the day</td><td style={{ ...td, textAlign: "right" }}>{fmt(totals.cashSpent)}</td></tr>
          <tr><td style={{ ...td, fontWeight: 700 }}>Balance as should be</td><td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmt(totals.currentBalance)}</td></tr>
          <tr><td style={td}>Cash Balance as per Register</td><td style={{ ...td, textAlign: "right" }}>{fmt(totals.billsTotal)}</td></tr>
          <tr><td style={{ ...td, fontWeight: 700 }}>Over / Short</td><td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmt(totals.overShort)}</td></tr>
        </tbody>
      </table>

      {/* Bills (cash count) — kept at the end of the report */}
      <div style={{ fontWeight: 700, fontSize: 11, margin: "12px 0 4px" }}>6. Bills (cash count)</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={th}>Denomination</th>
            <th style={{ ...th, textAlign: "right" }}>Count</th>
            <th style={{ ...th, textAlign: "right" }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {totals.billsBreakdown.map((b) => (
            <tr key={b.denom}>
              <td style={td}>${b.denom}</td>
              <td style={{ ...td, textAlign: "right" }}>{b.count}</td>
              <td style={{ ...td, textAlign: "right" }}>{fmt(b.amount)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...td, fontWeight: 700 }}>Total</td>
            <td style={td} />
            <td style={{ ...td, textAlign: "right", fontWeight: 700 }}>{fmt(totals.billsTotal)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
});
